import React from 'react'
import type { ClientDatabase, Domain } from '@hpanel/web-common/api/types'
import { PhpMyAdminButton } from './PhpMyAdminButton'

/**
 * Tabular list of databases with row-level actions.
 *
 * Each row exposes:
 *   - Manage Users → opens ManageUsersModal (parent owns the modal state).
 *   - Open phpMyAdmin → uses PhpMyAdminButton, mints SSO link.
 *   - Rotate Password → opens RotatePasswordModal targeting the primary user.
 *   - Delete → opens DeleteDatabaseDialog.
 *
 * The phpMyAdmin button needs the *primary user's* db_user_id, which the
 * list response doesn't include (only `users[]` on the detail response
 * does). We pass `dbUserId={0}` as a sentinel meaning "primary user;
 * server resolves it" — the backend route accepts an optional user_id
 * query param and falls back to the primary if absent.
 */

export interface DatabaseListProps {
  databases: ClientDatabase[]
  domains: Domain[]
  onManageUsers: (db: ClientDatabase) => void
  onRotatePrimary: (db: ClientDatabase) => void
  onDelete: (db: ClientDatabase) => void
  /** Plan 18 §C — opens the ConnectionDetailsModal for this row. */
  onShowConnection: (db: ClientDatabase) => void
}

const statusBadge = (status: string): string => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
    case 'suspended':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    case 'creating':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
  }
}

const typeBadge = (type: string): string => {
  switch (type) {
    case 'mysql':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
  }
}

const fmtSize = (mb: number | null | undefined): string => {
  if (mb == null) return '—'
  if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`
  return `${mb.toFixed(1)} MB`
}

export const DatabaseList: React.FC<DatabaseListProps> = ({
  databases,
  domains,
  onManageUsers,
  onRotatePrimary,
  onDelete,
  onShowConnection,
}) => {
  const domainName = (id: number | null): string => {
    if (id == null) return '—'
    const d = domains.find((dd) => dd.id === id)
    return d ? d.name : `#${id}`
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Name
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Type
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Size
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Domain
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Status
              </th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {databases.map((db) => (
              <tr
                key={db.id}
                data-testid={`database-row-${db.id}`}
                className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <td className="py-3 px-4 text-sm font-mono font-medium text-gray-900 dark:text-gray-100">
                  {db.name}
                </td>
                <td className="py-3 px-4">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${typeBadge(db.db_type)}`}
                  >
                    {db.db_type === 'mysql' ? 'MySQL' : db.db_type}
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                  {fmtSize(db.size_mb)}
                </td>
                <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                  {domainName(db.domain_id)}
                </td>
                <td className="py-3 px-4">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${statusBadge(db.status)}`}
                  >
                    {db.status}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onShowConnection(db)}
                      data-testid={`connection-details-${db.id}`}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Connection details
                    </button>
                    <button
                      type="button"
                      onClick={() => onManageUsers(db)}
                      data-testid={`manage-users-${db.id}`}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Manage Users
                    </button>
                    <PhpMyAdminButton
                      dbId={db.id}
                      dbUserId={0}
                      label="phpMyAdmin"
                      variant="inline"
                    />
                    <button
                      type="button"
                      onClick={() => onRotatePrimary(db)}
                      data-testid={`rotate-${db.id}`}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Rotate Password
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(db)}
                      data-testid={`delete-${db.id}`}
                      className="text-sm text-red-600 dark:text-red-400 hover:underline"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
